import * as z from 'zod';

/**
 * Auslastungsprognose domain model. The Monte-Carlo math sits behind the repository seam
 * (`site-simulation.engine` today, a Laravel endpoint later), so the Zod schemas below stay
 * authoritative and a live endpoint can be validated without touching the component.
 *
 * Unit convention: every rate and share on {@link SimulationRequest} is a **fraction, not a
 * percentage** — `bevShare.mode = 0.04` is 4 %, `captureFactor = 0.0175` is a Turn-in-Rate of
 * 1,75 %, and the two split shares sum to 1. The host converts the percent values the planner
 * edits before calling the repository. `SegmentParams.windowHours` is the exception: an
 * absolute hour count.
 */

/**
 * Triangular-distribution input (spec §3, §4.1). `mode` is the most-likely value, not the
 * mean. Both the engine and the upstream `inputsValid` gate require `low ≤ mode ≤ high`.
 */
export interface ThreePoint {
  low: number;
  mode: number;
  high: number;
}

/**
 * Both segments share a run's DTV / BEV / LP / Leistung inputs and differ only by these two
 * values, plus the per-session energy constant the engine applies.
 */
export interface SegmentParams {
  /** Turn-in-Rate — fraction of passing BEVs that charge here (e.g. 0.0175 = 1,75 %). */
  captureFactor: number;
  /**
   * Zeitfenster — usable hours of the day in the E_max ceiling (spec §4.4). An absolute hour
   * count (e.g. 16), not a fraction.
   */
  windowHours: number;
}

/** Three-point inputs are sampled per iteration; single-value inputs are fixed for the run. */
export interface SimulationRequest {
  /** Daily traffic (DTV / vehicles per day). */
  dtv: ThreePoint;
  /** Base BEV share of traffic, as a fraction (mode 0.04 = 4 %). */
  bevShare: ThreePoint;
  /** Annual BEV-share growth, as a fraction (mode 0.10 = 10 %). */
  bevGrowth: ThreePoint;
  /** Ladepunkte. */
  lp: number;
  powerKw: number;
  /** Zwischenstopp. */
  onRoute: SegmentParams;
  /** Retail / Kundenparkplatz. */
  destination: SegmentParams;
  /**
   * Fractions summing to 1, weighting the two segment forecasts. The split dialogs enforce
   * the sum upstream; the engine guards its own boundary rather than silently normalizing.
   */
  destinationShare: number;
  onRouteShare: number;
}

/** Percentiles across the iterations, preserving `P10 ≤ P50 ≤ P90`. */
export interface YearUtilisation {
  /** 1-based: the base year is 1. */
  year: number;
  /** Mode-derived, as a fraction. */
  bevSharePct: number;
  sessionsP10: number;
  sessionsP50: number;
  sessionsP90: number;
  kwhPerCp: number;
  /** `E_total / E_max` per iteration, as fractions (spec §4.5). */
  energyUtilisationP10: number;
  energyUtilisationP50: number;
  energyUtilisationP90: number;
}

/**
 * The status-quo point estimate: a deterministic year-1 calculation from the mode inputs, not
 * a Monte-Carlo statistic.
 */
export interface HeadlineKpis {
  /** Energieauslastung — `E_total / E_max`, as a fraction. */
  energyUtilisationPct: number;
  /** kWh/LP/Tag. */
  kwhPerLpDay: number;
  /** Sessions/LP/Tag. */
  sessionsPerLpDay: number;
  /** Ø kWh/Session. Fixed per segment, so the blend is the share-weighted average. */
  avgKwhPerSession: number;
}

export interface SimulationResult {
  headline: HeadlineKpis;
  years: YearUtilisation[];
}

// --- Zod schemas -------------------------------------------------------------

export const threePointSchema = z.object({
  low: z.number(),
  mode: z.number(),
  high: z.number(),
}) satisfies z.ZodType<ThreePoint>;

/**
 * Lives in the model layer so the request schema, the engine's boundary guard and the host's
 * `inputsValid` gate all enforce the same threshold and cannot drift apart.
 */
export const SHARE_SUM_EPSILON = 1e-6;

export const segmentParamsSchema = z.object({
  captureFactor: z.number(),
  windowHours: z.number(),
}) satisfies z.ZodType<SegmentParams>;

export const simulationRequestSchema = z
  .object({
    dtv: threePointSchema,
    bevShare: threePointSchema,
    bevGrowth: threePointSchema,
    lp: z.number(),
    powerKw: z.number(),
    onRoute: segmentParamsSchema,
    destination: segmentParamsSchema,
    destinationShare: z.number().min(0).max(1),
    onRouteShare: z.number().min(0).max(1),
  })
  // A live endpoint should reject a non-normalized split as a 422 rather than let it reach
  // the engine and throw a 500. Per-share `[0,1]` bounds alone pass a `0.3 / 0.3` split.
  .refine(
    (r) =>
      Math.abs(r.destinationShare + r.onRouteShare - 1) <= SHARE_SUM_EPSILON,
    {
      message: 'onRoute/destination shares must sum to 1',
      path: ['onRouteShare'],
    },
  ) satisfies z.ZodType<SimulationRequest>;

export const yearUtilisationSchema = z.object({
  year: z.number(),
  bevSharePct: z.number(),
  sessionsP10: z.number(),
  sessionsP50: z.number(),
  sessionsP90: z.number(),
  kwhPerCp: z.number(),
  energyUtilisationP10: z.number(),
  energyUtilisationP50: z.number(),
  energyUtilisationP90: z.number(),
}) satisfies z.ZodType<YearUtilisation>;

export const headlineKpisSchema = z.object({
  energyUtilisationPct: z.number(),
  kwhPerLpDay: z.number(),
  sessionsPerLpDay: z.number(),
  avgKwhPerSession: z.number(),
}) satisfies z.ZodType<HeadlineKpis>;

export const simulationResultSchema = z.object({
  headline: headlineKpisSchema,
  years: z.array(yearUtilisationSchema),
}) satisfies z.ZodType<SimulationResult>;
